/**
 * Zine markdown notations — detection + parsing for the p2e "issue" class page.
 *
 * Two authoring conventions are recognized:
 *
 * 1. Vocabulary list — a markdown unordered list where EVERY item matches
 *    `**term** _category_ — definition` (category optional; separator may be
 *    an em dash, en dash, `--`, or a spaced hyphen; category may use
 *    `_underscores_` or `*asterisks*`).
 *
 * 2. Quote interlude — markdown that is entirely a blockquote whose final
 *    line starts with an em dash (`—`, `–`, or `--`) attribution.
 *
 * Both parsers return `null` when the text does not fully match the
 * convention, so callers can fall back to regular prose rendering.
 */

export interface VocabListItem {
  term: string;
  category?: string;
  definition: string;
}

export interface QuoteInterlude {
  quote: string;
  attribution: string;
}

/**
 * One vocab list item:
 *   `- **term** _category_ — definition`
 * - list marker: `-`, `*`, or `+` (up to 3 leading spaces)
 * - term: bold, may contain spaces
 * - category: optional, `_italic_` or `*italic*`
 * - separator: em dash / en dash (spaces optional) or `--` / `-`
 *   (surrounding spaces required so hyphenated terms don't false-match)
 * - definition: rest of the line (non-empty)
 */
const VOCAB_ITEM_PATTERN =
  /^\s{0,3}[-*+]\s+\*\*(.+?)\*\*\s*(?:_([^_]+)_|\*([^*]+)\*)?\s*(?:[—–]|(?<=\s)(?:--|-)(?=\s))\s*(.+)$/;

const BLOCKQUOTE_LINE_PATTERN = /^\s{0,3}>\s?(.*)$/;
const ATTRIBUTION_PATTERN = /^(?:—|–|--)\s*(.+)$/;

const QUOTE_PAIRS: Array<[string, string]> = [
  ['"', '"'],
  ['“', '”'], // “ ”
  ['‘', '’'], // ‘ ’
  ["'", "'"],
  ['«', '»'], // « »
];

const normalizeNewLines = (text: string | null | undefined) =>
  (text ?? '').replace(/\r\n/g, '\n');

/**
 * Detects and parses a vocabulary-list markdown text.
 *
 * Returns the parsed items when the entire text (ignoring surrounding blank
 * lines) is an unordered list whose every item matches
 * `**term** _category_ — definition`; otherwise returns `null`.
 */
export function parseVocabList(
  markdown: string | null | undefined,
): VocabListItem[] | null {
  const lines = normalizeNewLines(markdown).split('\n');
  const items: VocabListItem[] = [];

  for (const line of lines) {
    if (!line.trim()) {
      continue;
    }

    const match = line.match(VOCAB_ITEM_PATTERN);
    if (!match) {
      return null;
    }

    const [, term, underscoreCategory, asteriskCategory, definition] = match;
    const trimmedTerm = term?.trim() ?? '';
    const trimmedDefinition = definition?.trim() ?? '';
    if (!trimmedTerm || !trimmedDefinition) {
      return null;
    }

    const category = (underscoreCategory ?? asteriskCategory)?.trim();

    items.push({
      term: trimmedTerm,
      ...(category ? { category } : {}),
      definition: trimmedDefinition,
    });
  }

  return items.length > 0 ? items : null;
}

/**
 * Detects and parses a quote-interlude markdown text.
 *
 * Returns `{ quote, attribution }` when the entire text (ignoring
 * surrounding blank lines) is a blockquote whose last non-empty line begins
 * with an em dash / en dash / `--` attribution; otherwise returns `null`.
 *
 * The quote keeps its inline markdown untouched (so callers can render bold,
 * italic, or `==mark==` via the shared inline renderer); surrounding quote
 * marks — straight or curly — are stripped.
 */
export function parseQuoteInterlude(
  markdown: string | null | undefined,
): QuoteInterlude | null {
  const lines = normalizeNewLines(markdown).split('\n');

  const quotedLines: string[] = [];
  for (const line of lines) {
    if (!line.trim()) {
      continue;
    }

    const match = line.match(BLOCKQUOTE_LINE_PATTERN);
    if (!match) {
      return null;
    }

    quotedLines.push(match[1] ?? '');
  }

  // Drop trailing empty quoted lines (e.g. `>` spacers at the end).
  while (quotedLines.length && !quotedLines[quotedLines.length - 1].trim()) {
    quotedLines.pop();
  }

  if (quotedLines.length < 2) {
    return null;
  }

  const lastLine = quotedLines[quotedLines.length - 1].trim();
  const attributionMatch = lastLine.match(ATTRIBUTION_PATTERN);
  if (!attributionMatch) {
    return null;
  }

  const attribution = attributionMatch[1]?.trim() ?? '';
  if (!attribution) {
    return null;
  }

  const quote = stripSurroundingQuotes(
    quotedLines
      .slice(0, -1)
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n')
      .trim(),
  );

  if (!quote) {
    return null;
  }

  return { quote, attribution };
}

function stripSurroundingQuotes(text: string): string {
  for (const [open, close] of QUOTE_PAIRS) {
    if (
      text.length > open.length + close.length &&
      text.startsWith(open) &&
      text.endsWith(close)
    ) {
      return text.slice(open.length, -close.length).trim();
    }
  }
  return text;
}
